import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, CATEGORY_GROUPS } from "../lib/api";
import { DarkPortalShell, StatusBadge, AttachmentList, NewBadge } from "../components/Shell";
import { useNewFlags } from "../hooks/useNewFlags";
import ReviewWorkspace, { WORKING_STATUSES } from "../components/ReviewWorkspace";
import DocViewer from "../components/DocViewer";
import VideoModal from "../components/VideoModal";
import FeedbackForm from "../components/FeedbackForm";
import ManagerImplementation from "./ManagerImplementation";
import { DocSearchBar, filterDocs } from "../components/DocSearchBar";
import { Eye, MessageSquarePlus, Clock, UserCheck, Youtube, LayoutDashboard, BookOpen, Layers, MessageSquare, AlertTriangle } from "lucide-react";

const MGR_NAV = [
  { id: "progress", label: "Progress", icon: LayoutDashboard },
  { id: "documents", label: "Document Library", icon: BookOpen },
  { id: "implementation", label: "Implementation", icon: Layers },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
];

const StatCard = ({ label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl p-4 border"
    style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}
  >
    <p className="text-3xl font-heading font-bold" style={{ color: accent || "#fff" }}>{value}</p>
    <p className="text-xs text-[#9CA3AF] mt-1">{label}</p>
  </div>
);

export const DocRow = ({ d, onView, onWatch, showStatus = true, onWorkspace }) => {
  const { isNewForMe, markSeen } = useNewFlags();
  const view = (v) => { markSeen(d); onView({ ...v, allowDownload: d.downloadable }); };
  const openMain = () => {
    if (onWorkspace && WORKING_STATUSES.includes(d.status)) { markSeen(d); onWorkspace(d); return; }
    view({ file: d.main_file, title: d.title });
  };
  return (
  <div data-testid={`manager-doc-${d.id}`} className="px-5 py-4 border-t first:border-t-0" style={{ borderColor: "#1E3A8A" }}>
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <span className="flex items-center gap-2 min-w-0">
        <p className="text-white font-medium truncate min-w-0">{d.title}</p>
        {isNewForMe(d) && <NewBadge testId={`manager-new-${d.id}`} />}
      </span>
      <div className="flex items-center gap-3 flex-shrink-0">
        {showStatus && <StatusBadge status={d.status} />}
        {d.main_file && (
          <button data-testid={`manager-view-main-${d.id}`} onClick={openMain}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>
            <Eye size={14} /> View Document
          </button>
        )}
        {d.summary_sheet && (
          <button data-testid={`manager-view-summary-${d.id}`} onClick={() => view({ file: d.summary_sheet, title: `${d.title} — Summary` })}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-white border" style={{ borderColor: "#1E3A8A" }}>
            <Eye size={14} /> View Summary
          </button>
        )}
        {d.video_url && (
          <button data-testid={`manager-watch-video-${d.id}`} onClick={() => { markSeen(d); onWatch({ url: d.video_url, title: d.title }); }}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-white border" style={{ borderColor: "#1E3A8A" }}>
            <Youtube size={14} style={{ color: "#F97316" }} /> Watch Video
          </button>
        )}
      </div>
    </div>
    {(d.attachments || []).some((a) => !a.portals || a.portals.includes("manager")) && (
      <div className="mt-3 pl-1"><AttachmentList attachments={(d.attachments || []).filter((a) => !a.portals || a.portals.includes("manager"))} onView={view} /></div>
    )}
  </div>
  );
};

function ManagerNav({ items, view, onChange, badges }) {
  return (
    <nav className="flex gap-1 mb-8 overflow-x-auto p-1 rounded-xl" style={{ backgroundColor: "#112A57" }} data-testid="manager-nav">
      {items.map((n) => {
        const Icon = n.icon;
        const active = view === n.id;
        const badge = badges?.[n.id] || 0;
        return (
          <button key={n.id} data-testid={`manager-nav-${n.id}`} onClick={() => onChange(n.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
            style={active ? { backgroundColor: "#F97316", color: "#fff" } : { color: "#9CA3AF" }}>
            <Icon size={16} /> {n.label}
            {badge > 0 && (
              <span data-testid={`manager-nav-badge-${n.id}`} className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ backgroundColor: "#7C3AED", color: "#fff" }}>{badge}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

const MANAGER_STATS = [
  { testid: "stat-total", label: "Total Documents", key: "total", accent: "#F97316" },
  { testid: "stat-not-started", label: "Not Started", key: "Not Started", accent: "#9CA3AF" },
  { testid: "stat-in-progress", label: "In Progress", key: "In Progress", accent: "#3B82F6" },
  { testid: "stat-in-review", label: "In Review", key: "In Review", accent: "#F59E0B" },
  { testid: "stat-amending", label: "Amending", key: "Amending", accent: "#8B5CF6" },
  { testid: "stat-in-consultation", label: "In Consultation", key: "In Consultation", accent: "#14B8A6" },
  { testid: "stat-pending", label: "Pending Approval", key: "Pending Approval", accent: "#F97316" },
  { testid: "stat-approved", label: "Approved", key: "Approved", accent: "#10B981" },
];

function CompletionCard({ stats }) {
  return (
    <div className="rounded-xl p-5 border md:col-span-1" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <div className="flex items-end justify-between mb-2">
        <p className="text-sm text-[#9CA3AF]">Completion</p>
        <p data-testid="completion-percent" className="font-heading text-2xl font-bold text-white">{stats.pct}%</p>
      </div>
      <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: "#0A1F44" }}>
        <div data-testid="completion-bar" className="h-full rounded-full transition-all duration-700"
          style={{ width: `${stats.pct}%`, backgroundColor: "#F97316" }} />
      </div>
      <p className="text-xs text-[#9CA3AF] mt-2">{stats.c["Approved"]} of {stats.c.total} approved</p>
    </div>
  );
}

function WaitingCard({ testid, value, label, icon: Icon, color, bg }) {
  return (
    <div data-testid={testid} className="rounded-xl p-5 border flex items-center gap-4" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: bg }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="font-heading text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-[#9CA3AF]">{label}</p>
      </div>
    </div>
  );
}

function ProgressSection({ stats }) {
  return (
    <section data-testid="manager-progress">
      <h1 className="font-heading text-white text-3xl font-bold mb-5">Progress Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        {MANAGER_STATS.map((s) => (
          <StatCard key={s.testid} testid={s.testid} label={s.label} value={stats.c[s.key]} accent={s.accent} />
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <CompletionCard stats={stats} />
        <WaitingCard testid="waiting-admin" value={stats.waitingAdmin} label="Waiting on Admin"
          icon={Clock} color="#F97316" bg="rgba(249,115,22,0.15)" />
        <WaitingCard testid="waiting-client" value={stats.waitingClient} label="Waiting on Client"
          icon={UserCheck} color="#3B82F6" bg="rgba(59,130,246,0.15)" />
      </div>
    </section>
  );
}

function EmptyPanel({ children, testId }) {
  return (
    <div className="rounded-xl border p-12 text-center text-[#9CA3AF]"
      style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }} data-testid={testId}>{children}</div>
  );
}

function LibraryContent({ docs, filtered, onView, onWatch, onWorkspace }) {
  if (docs.length === 0) return <EmptyPanel>No documents yet.</EmptyPanel>;
  if (filtered.length === 0) return <EmptyPanel testId="manager-doc-no-match">No documents match your search.</EmptyPanel>;
  return (
    <div className="space-y-5">
      {CATEGORY_GROUPS.map((g) => {
        const groupDocs = filtered.filter((d) => d.category === g.cat);
        if (groupDocs.length === 0) return null;
        return (
          <div key={g.cat} data-testid={`doc-group-${g.cat}`}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#F97316" }} />
              <h3 className="font-heading text-white text-sm font-bold uppercase tracking-wider">{g.label}</h3>
              <span className="text-xs text-[#9CA3AF]">({groupDocs.length})</span>
            </div>
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
              {groupDocs.map((d) => <DocRow key={d.id} d={d} onView={onView} onWatch={onWatch} showStatus={d.category !== "Client uploads"} onWorkspace={onWorkspace} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DocumentLibrarySection({ docs, onView, onWatch, onWorkspace }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const filtered = filterDocs(docs, search, filterCat, filterStatus);
  return (
    <section data-testid="manager-documents">
      <h2 className="font-heading text-white text-2xl font-bold mb-4">Document Library</h2>
      <DocSearchBar search={search} setSearch={setSearch} category={filterCat} setCategory={setFilterCat}
        status={filterStatus} setStatus={setFilterStatus} dark testPrefix="manager-doc" />
      <LibraryContent docs={docs} filtered={filtered} onView={onView} onWatch={onWatch} onWorkspace={onWorkspace} />
    </section>
  );
}

function FeedbackSection({ clientId, docs, show, onToggle }) {
  return (
    <section data-testid="manager-feedback">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-white text-2xl font-bold">Submit Feedback</h2>
        <button data-testid="manager-feedback-toggle" onClick={onToggle}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg"
          style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}>
          <MessageSquarePlus size={16} /> {show ? "Hide" : "New Feedback"}
        </button>
      </div>
      {show && (
        <div className="rounded-xl p-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
          <div data-testid="feedback-decision-warning" className="flex items-start gap-3 rounded-lg border p-4 mb-5"
            style={{ backgroundColor: "rgba(249,115,22,0.1)", borderColor: "#F97316" }}>
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" style={{ color: "#F97316" }} />
            <p className="text-sm leading-relaxed" style={{ color: "#FDBA74" }}>
              <span className="font-semibold text-white">This is not where you answer client decisions.</span>{" "}
              Client decisions must be answered against each decision in the review portal. This is so that every
              decision is authorised by the people responsible for it, and so that it is recorded against the document
              it belongs to in your audit export. Use this box for general questions and comments only.
            </p>
          </div>
          <FeedbackForm clientId={clientId} documents={docs} />
        </div>
      )}
    </section>
  );
}

function computeDocStats(docs) {
  const c = { total: docs.length, "Not Started": 0, "In Progress": 0, "In Review": 0, "Amending": 0, "In Consultation": 0, "Pending Approval": 0, "Approved": 0 };
  docs.forEach((d) => { c[d.status] = (c[d.status] || 0) + 1; });
  const pct = c.total ? Math.round((c["Approved"] / c.total) * 100) : 0;
  const waitingAdmin = c["Not Started"] + c["In Progress"] + c["Amending"];
  const waitingClient = c["In Review"] + c["In Consultation"] + c["Pending Approval"];
  return { c, pct, waitingAdmin, waitingClient };
}

function buildManagerNav(features, readOnly) {
  return MGR_NAV.filter((n) => {
    if (n.id === "feedback" && readOnly) return false;
    if (n.id === "implementation" && !features.implementation) return false;
    return true;
  });
}

export function ManagerView({ clientId, readOnly = false }) {
  const { isNewForMe } = useNewFlags();
  const [docs, setDocs] = useState([]);
  const [features, setFeatures] = useState({ implementation: false, account: false });
  const [viewing, setViewing] = useState(null);
  const [watch, setWatch] = useState(null);
  const [workspaceDoc, setWorkspaceDoc] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [view, setView] = useState("progress");

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/documents`).then(({ data }) => setDocs(data));
    api.get(`/clients/${clientId}/features`).then(({ data }) => setFeatures(data));
  }, [clientId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // live dashboard
    return () => clearInterval(t);
  }, [load]);

  const stats = useMemo(() => computeDocStats(docs.filter((d) => d.category !== "Client uploads")), [docs]);

  const navItems = buildManagerNav(features, readOnly);
  const effectiveView = navItems.some((n) => n.id === view) ? view : "progress";
  const newDocCount = docs.filter(isNewForMe).length;

  return (
    <>
      <ManagerNav items={navItems} view={effectiveView} onChange={setView} badges={{ documents: newDocCount }} />

      {effectiveView === "progress" && <ProgressSection stats={stats} />}
      {effectiveView === "documents" && <DocumentLibrarySection docs={docs} onView={setViewing} onWatch={setWatch} onWorkspace={setWorkspaceDoc} />}
      {effectiveView === "implementation" && <ManagerImplementation clientId={clientId} readOnly={readOnly} />}
      {!readOnly && effectiveView === "feedback" && (
        <FeedbackSection clientId={clientId} docs={docs} show={showFeedback} onToggle={() => setShowFeedback((s) => !s)} />
      )}

      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload={!!viewing.allowDownload} onClose={() => setViewing(null)} />}
      {watch && <VideoModal url={watch.url} title={watch.title} onClose={() => setWatch(null)} />}
      {workspaceDoc && (
        <ReviewWorkspace doc={docs.find((x) => x.id === workspaceDoc.id) || workspaceDoc} clientId={clientId}
          role="manager" previewOnly={readOnly} onClose={() => setWorkspaceDoc(null)} />
      )}
    </>
  );
}

export default function ManagerPortal() {
  const { auth } = useAuth();
  return (
    <DarkPortalShell subtitle="Manager Portal">
      <ManagerView clientId={auth.client_id} />
    </DarkPortalShell>
  );
}
